// Platform language master — used by the subject master, platform language
// setting, MCQ language selection and the exam client. All rendering relies on
// Unicode + system Indic fonts (Nirmala UI ships with Windows 10/11 and covers
// every Indian script) — no external dependency.

export interface PlatformLanguage {
    code: string;
    label: string;
    script: 'latin' | 'devanagari' | 'gujarati' | 'bengali' | 'tamil' | 'telugu' | 'kannada' | 'malayalam' | 'gurmukhi' | 'odia' | 'arabic';
    rtl?: boolean;
}

export const PLATFORM_LANGUAGES: PlatformLanguage[] = [
    { code: 'english',   label: 'English',              script: 'latin' },
    { code: 'hindi',     label: 'हिन्दी (Hindi)',         script: 'devanagari' },
    { code: 'marathi',   label: 'मराठी (Marathi)',        script: 'devanagari' },
    { code: 'sanskrit',  label: 'संस्कृत (Sanskrit)',      script: 'devanagari' },
    { code: 'konkani',   label: 'कोंकणी (Konkani)',       script: 'devanagari' },
    { code: 'nepali',    label: 'नेपाली (Nepali)',         script: 'devanagari' },
    { code: 'gujarati',  label: 'ગુજરાતી (Gujarati)',      script: 'gujarati' },
    { code: 'bengali',   label: 'বাংলা (Bengali)',        script: 'bengali' },
    { code: 'assamese',  label: 'অসমীয়া (Assamese)',      script: 'bengali' },
    { code: 'tamil',     label: 'தமிழ் (Tamil)',          script: 'tamil' },
    { code: 'telugu',    label: 'తెలుగు (Telugu)',        script: 'telugu' },
    { code: 'kannada',   label: 'ಕನ್ನಡ (Kannada)',        script: 'kannada' },
    { code: 'malayalam', label: 'മലയാളം (Malayalam)',    script: 'malayalam' },
    { code: 'punjabi',   label: 'ਪੰਜਾਬੀ (Punjabi)',        script: 'gurmukhi' },
    { code: 'odia',      label: 'ଓଡ଼ିଆ (Odia)',           script: 'odia' },
    { code: 'urdu',      label: 'اردو (Urdu)',           script: 'arabic', rtl: true },
    { code: 'arabic',    label: 'العربية (Arabic)',       script: 'arabic', rtl: true },
    { code: 'spanish',   label: 'Español (Spanish)',     script: 'latin' },
    { code: 'french',    label: 'Français (French)',     script: 'latin' },
    { code: 'german',    label: 'Deutsch (German)',      script: 'latin' },
];

// Font stack per script — system fonts present on Windows 10/11
export const SCRIPT_FONTS: Record<string, string> = {
    latin:      "'Segoe UI', system-ui, sans-serif",
    devanagari: "'Nirmala UI', 'Mangal', 'Noto Sans Devanagari', sans-serif",
    gujarati:   "'Nirmala UI', 'Shruti', sans-serif",
    bengali:    "'Nirmala UI', 'Vrinda', sans-serif",
    tamil:      "'Nirmala UI', 'Latha', sans-serif",
    telugu:     "'Nirmala UI', 'Gautami', sans-serif",
    kannada:    "'Nirmala UI', 'Tunga', sans-serif",
    malayalam:  "'Nirmala UI', 'Kartika', sans-serif",
    gurmukhi:   "'Nirmala UI', 'Raavi', sans-serif",
    odia:       "'Nirmala UI', 'Kalinga', sans-serif",
    arabic:     "'Segoe UI', 'Traditional Arabic', sans-serif",
};

export function languageFont(code?: string): string {
    const lang = PLATFORM_LANGUAGES.find(l => l.code === String(code || 'english').toLowerCase());
    return SCRIPT_FONTS[lang?.script || 'latin'];
}
